import React, { useEffect, useRef, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./Products.css";

gsap.registerPlugin(ScrollTrigger);

const Products = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const productsRef = useRef([]);
  const cursorRef = useRef(null);
  const cursorDotRef = useRef(null);
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const products = [
    {
      id: 1,
      name: "eBin",
      description: "E-Waste Management Platform",
      icon: "4.svg",
      color: "#10B981", // Green
      gradient: "linear-gradient(135deg, #10B981, #059669)",
    },
    {
      id: 2,
      name: "IIMS",
      description: "Impact Investment Management System",
      icon: "iims.svg",
      color: "#06074d", // Blue
      gradient: "linear-gradient(135deg, #06074d, #05063a)",
    },
    {
      id: 3,
      name: "YAEI-Connect",
      description: "CRM used to manage Youth Initiatives.",
      icon: "1.svg",
      color: "#F59E0B", // Amber
      gradient: "linear-gradient(135deg, #F59E0B, #D97706)",
    },
    {
      id: 4,
      name: "AI-DSS",
      description: "AI Decision Support System for Healthcare",
      icon: "2.svg",
      color: "#8B5CF6", // Purple
      gradient: "linear-gradient(135deg, #8B5CF6, #7C3AED)",
    },
    {
      id: 5,
      name: "FlexiHub",
      description: "FinTech Platform for Business Management",
      icon: "5.png",
      color: "#ecb946", // Pink
      gradient: "linear-gradient(135deg, #EC4899, #DB2777)",
    },
  ];

  useEffect(() => {
    // Check if mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);

    // Header animation
    gsap.fromTo(
      headerRef.current,
      { y: 80, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1.2,
        ease: "power3.out",
        scrollTrigger: {
          trigger: headerRef.current,
          start: "top 85%",
          toggleActions: "play none none reverse",
        },
      }
    );

    // Product cards stagger animation
    gsap.fromTo(
      productsRef.current,
      { y: 100, opacity: 0, scale: 0.8, rotationY: -20 },
      {
        y: 0,
        opacity: 1,
        scale: 1,
        rotationY: 0,
        duration: 1,
        stagger: 0.15,
        ease: "back.out(1.7)",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 75%",
          toggleActions: "play none none reverse",
        },
      }
    );

    // Desktop cursor follow effect
    if (!isMobile) {
      const handleMouseMove = (e) => {
        gsap.to(cursorRef.current, {
          x: e.clientX,
          y: e.clientY,
          duration: 0.3,
          ease: "power2.out",
        });

        gsap.to(cursorDotRef.current, {
          x: e.clientX,
          y: e.clientY,
          duration: 0.1,
        });
      };

      window.addEventListener("mousemove", handleMouseMove);

      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("resize", checkMobile);
      };
    }

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, [isMobile]);

  const handleProductHover = (index, isEntering) => {
    const product = productsRef.current[index];
    if (!product) return;

    if (isEntering) {
      setHoveredProduct(index);

      // Desktop: Magnetic pull + rotate
      if (!isMobile) {
        gsap.to(product, {
          scale: 1.15,
          rotationZ: gsap.utils.random(-5, 5),
          duration: 0.4,
          ease: "back.out(2)",
        });

        // Pulse the cursor
        gsap.to(cursorRef.current, {
          scale: 2,
          duration: 0.3,
        });
      } else {
        // Mobile: Shake and zoom
        gsap.to(product, {
          scale: 1.1,
          duration: 0.3,
          ease: "power2.out",
        });

        // Shake effect
        gsap.to(product, {
          x: -5,
          duration: 0.1,
          repeat: 3,
          yoyo: true,
          ease: "power1.inOut",
        });
      }
    } else {
      setHoveredProduct(null);

      gsap.to(product, {
        scale: 1,
        rotationZ: 0,
        x: 0,
        duration: 0.4,
        ease: "power2.out",
      });

      if (!isMobile) {
        gsap.to(cursorRef.current, {
          scale: 1,
          duration: 0.3,
        });
      }
    }
  };

  return (
    <section
      ref={sectionRef}
      className="products-section"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
        position: "relative",
        overflow: "hidden",
        padding: "6rem 0",
      }}
      id="products"
    >
      {/* Custom Cursor (Desktop only) */}
      {!isMobile && (
        <>
          <div ref={cursorRef} className="custom-cursor">
            {hoveredProduct !== null && (
              <span className="cursor-text">Explore</span>
            )}
          </div>
          <div ref={cursorDotRef} className="custom-cursor-dot"></div>
        </>
      )}

      {/* Background Decoration */}
      <div className="products-bg-decoration">
        <div className="bg-circle bg-circle-1"></div>
        <div className="bg-circle bg-circle-2"></div>
        <div className="bg-circle bg-circle-3"></div>
      </div>

      <Container className="position-relative" style={{ zIndex: 2 }}>
        {/* Section Header */}
        <div ref={headerRef} className="text-center mb-5">
          {/* <div
            style={{
              background: "linear-gradient(135deg, rgba(105, 54, 244, 0.1), rgba(139, 92, 246, 0.1))",
              border: "1px solid rgba(105, 54, 244, 0.2)",
              display: "inline-block",
              padding: "0.5rem 1rem",
              borderRadius: "50px",
              marginBottom: "1rem",
            }}
          >
            <i
              className="fa-solid fa-cube"
              style={{ color: "#6936F4", marginRight: "0.5rem" }}
            ></i>
            <span
              style={{
                color: "#6936F4",
                fontSize: "0.875rem",
                fontWeight: "600",
              }}
            >
              Our Innovation
            </span>
          </div> */}

          <h2
            className="gradient-text"
            style={{
              fontSize: "3rem",
              fontWeight: "bold",
              marginBottom: "1rem",
              color: "#0F0C1F",
            }}
          >
            In-House Products
          </h2>
          <p
            style={{
              color: "#6B7280",
              maxWidth: "700px",
              margin: "0 auto",
              fontSize: "1.125rem",
            }}
          >
            Cutting-edge solutions designed and developed by our team to transform
            industries and solve real-world challenges.
          </p>
        </div>

        {/* Products Grid */}
        <Row className="justify-content-center align-items-center g-4">
          {products.map((product, index) => (
            <Col
              key={product.id}
              xs={6}
              sm={6}
              md={4}
              lg={2}
              className="d-flex justify-content-center"
            >
              <div
                ref={(el) => (productsRef.current[index] = el)}
                className="product-card"
                onMouseEnter={() => handleProductHover(index, true)}
                onMouseLeave={() => handleProductHover(index, false)}
                style={{
                  borderColor: product.color,
                }}
              >
                {/* Glow Effect */}
                <div
                  className="product-glow"
                  style={{
                    background: product.gradient,
                    opacity: hoveredProduct === index ? 0.3 : 0,
                  }}
                ></div>

                {/* Icon Container */}
                <div className="product-icon-container">
                  <div
                    className="product-icon-bg"
                    style={{ background: product.gradient }}
                  ></div>
                  <img
                    src={`${process.env.PUBLIC_URL}/icons/products/${product.icon}`}
                    alt={product.name}
                    className="product-icon"
                  />
                </div>

                {/* Product Info */}
                <h4
                  className="product-name"
                  style={{ color: product.color }}
                >
                  {product.name}
                </h4>
                <p className="product-description">{product.description}</p>

                {/* Hover Indicator */}
                {/* <div
                  className="product-arrow"
                  style={{
                    opacity: hoveredProduct === index ? 1 : 0,
                    color: product.color,
                  }}
                >
                  <i className="fa-solid fa-arrow-right"></i>
                </div> */}
              </div>
            </Col>
          ))}
        </Row>

        {/* Bottom CTA */}
        {/* <div className="text-center mt-5 pt-4">
          <p style={{ color: "#6B7280", marginBottom: "1rem" }}>
            Interested in our products?
          </p>
          
            href="#contact1"
            className="products-cta-button"
          <a>
            <span>Get in Touch</span>
            <i className="fa-solid fa-chevron-right"></i>
          </a>
        </div> */}
      </Container>
    </section>
  );
};

export default Products;